import { useState } from "react";
import { supabase } from "../supabaseClient";
import Modal from "./Modal.jsx";
import { getBabaPhotoCandidates, handleBabaPhotoError, BABA_PLACEHOLDER } from "../utils/babaPhoto.js";
import { Star, Loader2, Check } from "lucide-react";

/**
 * Modal de avaliação disparado pelo botão "Avaliar" do CardSolicitacao.
 *
 * props:
 *   isOpen, onClose
 *   solicitacao  → { id, baba_id, baba_nome, baba_foto_url }
 *   familiaId    → id da família logada
 *   onAvaliada(solicitacaoId) → callback após inserir com sucesso
 */
export default function ModalAvaliacao({ isOpen, onClose, solicitacao, familiaId, onAvaliada }) {
  const [nota, setNota] = useState(0);
  const [hover, setHover] = useState(0);
  const [comentario, setComentario] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState(false);

  if (!solicitacao) return null;

  const foto = getBabaPhotoCandidates(solicitacao.baba_foto_url)[0] || BABA_PLACEHOLDER;

  const enviar = async () => {
    setErro("");
    if (nota < 1) {
      setErro("Escolha de 1 a 5 estrelas.");
      return;
    }
    setEnviando(true);
    const { error } = await supabase.from("avaliacoes").insert({
      solicitacao_id: solicitacao.id,
      familia_id: familiaId,
      baba_id: solicitacao.baba_id,
      nota,
      comentario: comentario.trim() || null,
    });
    setEnviando(false);
    if (error) {
      setErro("Não foi possível enviar a avaliação. Tente novamente.");
      return;
    }
    setSucesso(true);
    onAvaliada?.(solicitacao.id);
  };

  const fechar = () => {
    onClose?.();
    setTimeout(() => {
      setNota(0);
      setHover(0);
      setComentario("");
      setErro("");
      setSucesso(false);
    }, 200);
  };

  return (
    <Modal isOpen={isOpen} onClose={fechar} title="Avaliar experiência">
      {sucesso ? (
        <div className="text-center py-2">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <Check size={24} />
          </div>
          <p className="text-sm font-semibold text-warm-900 mb-1">Obrigada pela avaliação!</p>
          <p className="text-sm text-warm-500 mb-5">Ela ajuda outras famílias.</p>
          <button
            className="w-full rounded-xl bg-[var(--color-rose-400)] py-2.5 text-sm font-semibold text-white hover:brightness-105 transition"
            onClick={fechar}
          >
            Fechar
          </button>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img
              src={foto}
              alt={solicitacao.baba_nome}
              className="h-14 w-14 rounded-xl object-cover shrink-0"
              onError={(e) => handleBabaPhotoError(e, getBabaPhotoCandidates(solicitacao.baba_foto_url))}
            />
            <p className="text-sm font-semibold text-warm-900">
              Como foi sua experiência com {solicitacao.baba_nome || "a babá"}?
            </p>
          </div>

          <div className="flex items-center justify-center gap-2 mb-5">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setNota(n)}
                aria-label={`${n} estrela${n > 1 ? "s" : ""}`}
                className="transition-transform hover:scale-110"
              >
                <Star
                  size={32}
                  className={
                    n <= (hover || nota)
                      ? "fill-amber-400 text-amber-400"
                      : "fill-warm-200 text-warm-200"
                  }
                />
              </button>
            ))}
          </div>

          <label className="block text-xs font-semibold text-warm-600 mb-1.5">
            Conta um pouco sobre sua experiência (opcional)
          </label>
          <textarea
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            rows={3}
            placeholder="A babá foi pontual, atenciosa…"
            className="w-full rounded-xl border border-warm-200 px-3 py-2 text-sm text-warm-800 placeholder:text-warm-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-rose-400)]/40 resize-none mb-4"
          />

          {erro && <p className="text-xs text-red-500 mb-3">{erro}</p>}

          <button
            className="w-full rounded-xl bg-[var(--color-rose-400)] py-2.5 text-sm font-semibold text-white hover:brightness-105 transition disabled:opacity-60"
            onClick={enviar}
            disabled={enviando}
          >
            {enviando ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" /> Enviando…
              </span>
            ) : (
              "Enviar avaliação"
            )}
          </button>
        </div>
      )}
    </Modal>
  );
}
